import { ref, computed, onMounted, nextTick } from 'vue'
import { scrollToFirstError } from '../../foundation/js/inputValidators/scrollToFirstError'
import { useStore } from 'vuex'
import emailValidator from '../../foundation/js/inputValidators/emailValidator'
import phoneValidator from '../../foundation/js/inputValidators/phoneValidator'
import nameValidator from '../../foundation/js/inputValidators/nameValidator'
import cprValidator from '../../foundation/js/inputValidators/cprValidator'
import textAreaValidator from '../../foundation/js/inputValidators/textAreaValidator'
import stripHtml from 'foundation/js/stripHtml'
import FormInputTextField from 'components/FormInputTextField/FormInputTextField.vue'
import TooltipComponent from 'components/TooltipComponent/TooltipComponent.vue'
import FormAddressField from 'components/FormAddressField/FormAddressField.vue'
import FormInputRichText from 'components/FormInputRichText/FormInputRichText.vue'

export default {
    name: 'FundraiseForm',
    components: {
        FormInputTextField,
        FormInputRichText,
        TooltipComponent,
        FormAddressField
    },
    props: {
        fundraisingCampaignId: {
            type: String,
            required: true
        },
        cancelUrl: {
            type: String,
            required: false
        },
        isMindegave: {
            type: Boolean,
            required: false
        },
        draft: {
            type: Object,
            required: false
        },
        showUnsavedChangesWarning: {
            type: Boolean,
            required: false
        }

    },
    setup(props) {
        const store = useStore()
        const donationSettings = computed(
            () => store.state.fundraising.donation_settings
        )
        const pageId = computed(() => store.state.main.page_id)
        const getLabel = (labelKey) =>
            store.getters['labels/getLabel']({
                labelCollection: 'fundraiseForm',
                labelKey: labelKey
            })
        const isSubmitting = computed(
            () => store.state.fundraising.isSubmitting
        )

        const showUnsavedChangesWarning = props.showUnsavedChangesWarning;

        const handleBeforeUnload = (e) => {
            if (store.state.fundraising.isEditing && showUnsavedChangesWarning) {
                e.preventDefault();
                e.returnValue = ''; 
            }
        };
        
        if (showUnsavedChangesWarning) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        } else {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        }

        const unSubmitted = ref(true)

        const firstNameInput = ref(props.draft?.contactFirstName ?? '')
        const firstNameInputFilled = computed(() => {
            return unSubmitted.value && !firstNameInput.value
                ? true
                : nameValidator(firstNameInput.value)
        })

        const lastNameInput = ref(props.draft?.contactLastName ?? '')
        const lastNameInputFilled = computed(() => {
            return unSubmitted.value && !lastNameInput.value
                ? true
                : nameValidator(lastNameInput.value)
        })

        const phoneInput = ref(props.draft?.mobilePhone ?? '')
        const phoneInputFilled = computed(() => {
            return unSubmitted.value ? true : phoneValidator(phoneInput.value)
        })

        const emailInput = ref(props.draft?.email ?? '')
        const emailInputFilled = computed(() => {
            return unSubmitted.value ? true : emailValidator(emailInput.value)
        })

        const cprNumberInput = ref(props.draft?.cprNo ?? '')
        const cprNumberInputFilled = computed(() => {
            return unSubmitted.value ? true : cprValidator(cprNumberInput.value)
        })

        const cvrNumberInput = ref(props.draft?.cvrNo ?? '')
        const cvrNumberInputFilled = computed(() => {
            return unSubmitted.value ? true : cvrNumberInput.value.length > 2
        })

        const addressLineInput = ref(props.draft?.addressLine1 ?? '')
        const addressLineInputFilled = computed(() => {
            return unSubmitted.value && !addressLineInput.value
                ? true
                : addressLineInput.value.length > 2 &&
                      textAreaValidator(addressLineInput.value)
        })

        const postalCodeInput = ref(props.draft?.postalCode ?? '')
        const postalCodeInputFilled = computed(() => {
            return unSubmitted.value && !addressLineInput.value
                ? true
                : postalCodeInput.value.length === 4 &&
                      textAreaValidator(addressLineInput.value)
        })

        const cityInput = ref(props.draft?.city ?? '')
        const cityInputFilled = computed(() => {
            return unSubmitted.value && !addressLineInput.value
                ? true
                : cityInput.value.length > 2 &&
                      textAreaValidator(addressLineInput.value)
        })

        const paymentMethodInput = ref(props.draft?.paymentMethod ?? '')
        const paymentMethodInputFilled = computed(() => {
            return unSubmitted.value ? true : !!paymentMethodInput.value
        })

        const anonymousDonationInput = ref(props.draft?.isAnonymous ? '1' : '0')
        const isAnonymous = computed(() => anonymousDonationInput.value === '1')

        const customerTypeInput = ref(
            props.draft?.customerType == 0 ? 'Company' : 'Private'
        )
        const isPrivate = computed(() => customerTypeInput.value === 'Private')
        const isCompany = computed(() => customerTypeInput.value === 'Company')
        const companyNameInput = ref(props.draft?.companyName ?? '')

        const companyNameInputFilled = computed(() => {
            return unSubmitted.value && !companyNameInput.value ? true : textAreaValidator(companyNameInput.value)
        })

        const commentsInput = ref(props.draft?.comments ?? '')
        const commentsInputFilled = computed(() => {
            const text = stripHtml(commentsInput.value)
            if (unSubmitted.value) return true
            else return !!text
        })

        const taxDeductibleInput = ref(props.draft?.isTaxDeductible ?? false)

        const donationAmountInput = ref('')
        const donationAmountInputFilled = computed(() => {
            return unSubmitted.value ? true : donationAmountInput.value
        })

        const customDonationAmountInput = ref(donationAmountInput.value)
        const customDonationAmountInputIsValid = computed(() => {
            return unSubmitted.value && !customDonation.value
                ? true
                : customDonationAmountInput.value >=
                      (donationSettings.value.minimumAmount ?? 1)
        })

        const customDonation = ref(false)
        const valueinputfield = ref(null)

        const toggleCustomDonation = async function () {
            customDonation.value = !customDonation.value
            if (customDonation.value) {
                await enableCustomDonation()
            }
            else {
                donationAmountInput.value = props.draft?.totalPrice ?? donationSettings.value.presets[donationSettings.value.defaultPreset]
                disableCustomDonation()
            }
        }

        const enableCustomDonation = async function () {
            donationAmountInput.value = ''
            await nextTick()
            valueinputfield.value.focus()
        }

        const disableCustomDonation = function () {
            customDonation.value = false
            customDonationAmountInput.value = ''
        }

        const addressIsRequired = computed(
            () => isCompany.value || props.isMindegave
        )
        const cprIsRequired = computed(
            () => isPrivate.value && taxDeductibleInput.value
        )
        const cvrIsRequired = computed(() => isCompany.value)

        const inputIsValid = function () {
            var addressFilled =
                addressLineInputFilled.value &&
                postalCodeInputFilled.value &&
                cityInputFilled.value
            return customDonation.value
                ? customDonationAmountInputIsValid.value
                : true &&
                      (isPrivate.value || companyNameInputFilled.value) &&
                      firstNameInputFilled.value &&
                      lastNameInputFilled.value &&
                      phoneInputFilled.value &&
                      emailInputFilled.value &&
                      commentsInputFilled.value &&
                      (!cprIsRequired.value || cprNumberInputFilled.value) &&
                      (!cvrIsRequired.value || cvrNumberInputFilled.value) &&
                      (!addressIsRequired.value || addressFilled) &&
                      paymentMethodInputFilled.value
        }

        onMounted(() => {
            if (props.draft?.totalPrice) {
                var draftPreset = donationSettings.value.presets.find(
                    (val) => val == props.draft?.totalPrice
                )
                if (draftPreset) {
                    donationAmountInput.value = props.draft?.totalPrice
                } else {
                    customDonationAmountInput.value = props.draft?.totalPrice
                    customDonation.value = true
                }
            } else {
                donationAmountInput.value =
                    props.draft?.totalPrice ??
                    donationSettings.value.presets[
                        donationSettings.value.defaultPreset
                    ]
            }
        })

        const onSubmit = function () {
            unSubmitted.value = false
            if (!inputIsValid()) {
                scrollToFirstError('validation-error')
                return false;
            }

            const donationPayload = {
                pageId: pageId.value,
                paymentMethod: paymentMethodInput.value,
                totalPrice:
                    customDonationAmountInput.value ||
                    donationAmountInput.value,
                addressLine1: addressIsRequired.value
                    ? addressLineInput.value
                    : null,
                postalCode: addressIsRequired.value
                    ? postalCodeInput.value
                    : null,
                city: addressIsRequired.value ? cityInput.value : null,
                mobilePhone: phoneInput.value,
                email: emailInput.value,
                companyName: companyNameInput.value,
                contactFirstName: firstNameInput.value,
                contactLastName: lastNameInput.value,
                cprNo: cprIsRequired ? cprNumberInput.value : null,
                cvrNo: cvrIsRequired ? cvrNumberInput.value : null,
                comments: stripHtml(commentsInput.value),
                isTaxDeductible: taxDeductibleInput.value,
                isAnonymous: isAnonymous.value,
                customerType: customerTypeInput.value,
                referrer: document.referrer
            }

            store.dispatch('fundraising/submitPersonalDonation2', {
                fundraisingCampaignId: props.fundraisingCampaignId,
                payload: donationPayload
            })

            console.log('donationPayload', donationPayload)
        }

        const showCpr = computed(
            () => isPrivate.value && taxDeductibleInput.value
        )
        const showAddress = computed(() => isCompany.value || props.isMindegave)

        return {
            getLabel,
            donationSettings,
            companyNameInput,
            firstNameInput,
            lastNameInput,
            phoneInput,
            emailInput,
            addressLineInput,
            postalCodeInput,
            cityInput,
            cprNumberInput,
            cvrNumberInput,
            paymentMethodInput,
            anonymousDonationInput,
            isAnonymous,
            commentsInput,
            taxDeductibleInput,
            companyNameInputFilled,
            firstNameInputFilled,
            lastNameInputFilled,
            phoneInputFilled,
            emailInputFilled,
            addressLineInputFilled,
            postalCodeInputFilled,
            cityInputFilled,
            cprNumberInputFilled,
            cvrNumberInputFilled,
            paymentMethodInputFilled,
            donationAmountInput,
            donationAmountInputFilled,
            customDonation,
            customDonationAmountInput,
            customDonationAmountInputIsValid,
            commentsInputFilled,
            toggleCustomDonation,
            disableCustomDonation,
            cancelUrl: props.cancelUrl,
            onSubmit,
            showCpr,
            isSubmitting,
            valueinputfield,
            customerTypeInput,
            isPrivate,
            isCompany,
            showAddress
        }
    }
}
